import type { RefObject } from 'react';
import { useCallback, useRef, useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { usePromotePageData, usePromotePageState } from 'src/app/pages/promote/pageContext';
import { useStripeForm } from 'src/app/shared/components/payments';
import type { StripeValidationErrors } from 'src/app/shared/utils/payments';

import { useSharedHooks } from '../../../../../../hooks/useSharedHooks';

type Result = ModelHookResult<
	{
		loading: boolean;
		disabled: boolean;
		ref: RefObject<HTMLSpanElement | null>;
		validationErrors: StripeValidationErrors;
		error?: {
			title: string;
			description: string;
		};
	},
	{
		handleSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
		onCardDetailsFieldFocus: () => void;
		setValidationErrors: React.Dispatch<React.SetStateAction<StripeValidationErrors>>;
	}
>;

export function useNewCreditCard(): Result {
	const [{ email, name, paymentMethodSaveOptions, formState }] = usePromotePageState();

	const {
		data: {
			currency: { code },
			user: {
				country: { countryCode },
			},
		},
	} = usePromotePageData();

	const track = useTracking();
	const ref = useRef<HTMLSpanElement>(null);
	const { inProgress, ...rest } = useSharedHooks();

	const trackCardFieldFocus = useCallback(
		(field: 'cvc' | 'expiry' | 'number' | 'card') => {
			void track(`pp_focus_card_${field}`);
		},
		[track],
	);

	const stripeForm = useStripeForm({
		email,
		name,
		paymentMethodSaveOptions,
		countryCode,
		currency: code,
		...rest,
	});

	const { translate } = useI18n();

	const { errorMessage, errorType } = stripeForm.data;

	const error = useMemo(() => {
		if (!errorType) return undefined;
		if (errorType === 'api_connection_error') {
			return {
				title: translate('corgi.payments.stripe_error.connection_error_heading'),
				description: errorMessage || translate('corgi.payments.stripe_error.generic_error_description'),
			};
		}
		return {
			title: translate('corgi.payments.stripe_error.generic_decline_error_heading'),
			description: translate('corgi.payments.stripe_error.generic_error_description'),
		};
	}, [errorType, errorMessage, translate]);

	return {
		data: {
			ref,
			loading: inProgress,
			disabled: inProgress || formState.status === 'loading',
			validationErrors: stripeForm.data.validationErrors,
			error,
		},
		actions: {
			handleSubmit: stripeForm.actions.handleSubmit,
			onCardDetailsFieldFocus: () => trackCardFieldFocus('card'),
			setValidationErrors: stripeForm.actions.setValidationErrors,
		},
	};
}
